import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import {
  CheckCircle2, Loader2, Upload, ShieldCheck, Video, X, AlertTriangle, Monitor,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PARALLEL_PARTS = 3;   // upload 3 parts at once — fast without saturating home internet
const MAX_RETRIES = 4;
const RAW_CHUNK_HINT = 100 * 1024 * 1024;  // matches backend RAW_UPLOAD_CHUNK_SIZE

// Camera ops are meant to upload from a laptop/desktop, not a phone. Anything
// with a small touch screen gets a friendly "use a computer" screen — trying
// to background a multi-hour raw upload from a phone browser is a bad time.
const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isTouch = navigator.maxTouchPoints > 1 || 'ontouchstart' in window;
  const narrow = window.innerWidth < 900;
  const uaMobile = /Android|iPhone|iPad|iPod|Mobile|IEMobile|Opera Mini/i.test(ua);
  return uaMobile || (isTouch && narrow);
};

// ---- Public brand header (same as TVPage) ---------------------------------
const Brand = () => (
  <div className="flex items-center justify-center gap-3 mb-8">
    <img
      src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png"
      alt="Elevated Roping Media"
      className="h-14 md:h-16 w-auto drop-shadow-[0_0_18px_rgba(217,119,74,0.35)]"
    />
    <div className="text-left leading-none">
      <div className="font-black text-white text-xl md:text-2xl tracking-tight">ELEVATED</div>
      <div className="font-bold text-white/60 text-xs md:text-sm tracking-[0.25em] mt-1">
        CAMERA DROP
      </div>
    </div>
  </div>
);

// ---- Multipart uploader ---------------------------------------------------
async function uploadPart(url, blob, attempt = 0) {
  try {
    const r = await fetch(url, { method: 'PUT', body: blob });
    if (!r.ok) throw new Error(`Part upload failed: ${r.status}`);
    const etag = r.headers.get('ETag') || r.headers.get('etag');
    if (!etag) throw new Error('S3 did not return ETag (check bucket CORS: ExposeHeaders must include ETag)');
    return etag.replace(/"/g, '');
  } catch (err) {
    if (attempt >= MAX_RETRIES) throw err;
    const delay = 1000 * Math.pow(2, attempt);
    await new Promise(r => setTimeout(r, delay));
    return uploadPart(url, blob, attempt + 1);
  }
}

async function runMultipartUpload({ file, initResp, onPartComplete, isCancelledRef }) {
  const { chunk_size, part_urls } = initResp;
  const partResults = new Array(part_urls.length);
  let nextIndex = 0;

  const worker = async () => {
    while (true) {
      if (isCancelledRef.current) throw new Error('cancelled');
      const i = nextIndex++;
      if (i >= part_urls.length) return;
      const { part_number, url } = part_urls[i];
      const start = (part_number - 1) * chunk_size;
      const end = Math.min(start + chunk_size, file.size);
      const blob = file.slice(start, end);
      const etag = await uploadPart(url, blob);
      partResults[i] = { part_number, etag };
      onPartComplete(i + 1, part_urls.length);
    }
  };

  await Promise.all(Array.from({ length: PARALLEL_PARTS }, () => worker()));
  return partResults;
}

// ---- Human byte formatter -------------------------------------------------
const fmtBytes = (n) => {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
};

// ==========================================================================
export default function RawUploadPage() {
  const [isMobile, setIsMobile] = useState(() => isMobileDevice());
  const [overrideMobile, setOverrideMobile] = useState(false);

  // Re-check on resize so a browser dev-tools resize also flips it correctly.
  useEffect(() => {
    const onResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [authed, setAuthed] = useState(false);

  const [uploaderName, setUploaderName] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  // Default: today's date, current time — rounded to nearest minute.
  const defaultDT = useMemo(() => {
    const d = new Date();
    d.setSeconds(0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);
  const [eventDateTime, setEventDateTime] = useState(defaultDT);

  // Multi-file queue. Each entry: { id, file, status, progress:{done,total}, error }
  // status: 'pending' | 'uploading' | 'done' | 'error' | 'cancelled'
  const [queue, setQueue] = useState([]);
  const [running, setRunning] = useState(false);   // true while startAll() loop is active
  const [dragOver, setDragOver] = useState(false);
  const cancelledRef = useRef(false);
  const fileInputRef = useRef(null);


  const verifyPasscode = async (e) => {
    e.preventDefault();
    setPasscodeError('');
    if (passcode.length < 4) { setPasscodeError('Enter the passcode'); return; }
    setVerifying(true);
    try {
      await axios.post(`${API_URL}/api/raw/verify-passcode`, { passcode });
      setAuthed(true);
    } catch (err) {
      setPasscodeError(err.response?.data?.detail || 'Wrong passcode. Ask your producer.');
    } finally { setVerifying(false); }
  };

  // ---- File queue management ----
  const addFiles = useCallback((fileList) => {
    if (!fileList || fileList.length === 0) return;
    setQueue((prev) => {
      const existingKeys = new Set(prev.map((q) => `${q.file.name}::${q.file.size}::${q.file.lastModified}`));
      const additions = [];
      for (const f of fileList) {
        const key = `${f.name}::${f.size}::${f.lastModified}`;
        if (existingKeys.has(key)) continue;  // skip duplicates
        additions.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${additions.length}`,
          file: f,
          status: 'pending',
          progress: { done: 0, total: 0 },
          error: null,
        });
      }
      return [...prev, ...additions];
    });
  }, []);

  const onFilePick = (e) => addFiles(e.target.files);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
  };
  const removeFile = (id) => setQueue((prev) => prev.filter((q) => q.id !== id));

  const patchFile = useCallback((id, patch) => {
    setQueue((prev) => prev.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  }, []);

  const canStart = useMemo(() =>
    queue.some((q) => q.status === 'pending')
      && uploaderName.trim().length > 0
      && description.trim().length > 0
      && location.trim().length > 0
      && eventDateTime.length > 0
      && !running,
    [queue, uploaderName, description, location, eventDateTime, running]
  );

  const uploadOne = async (entry) => {
    let initResp = null;
    try {
      patchFile(entry.id, { status: 'uploading', progress: { done: 0, total: 0 }, error: null });
      const initR = await axios.post(`${API_URL}/api/raw/init-upload`, {
        passcode,
        uploader_name: uploaderName.trim(),
        description: description.trim(),
        location: location.trim(),
        event_datetime: eventDateTime,
        filename: entry.file.name,
        size: entry.file.size,
        content_type: entry.file.type || 'application/octet-stream',
      });
      initResp = initR.data;
      patchFile(entry.id, { progress: { done: 0, total: initResp.part_count } });

      const parts = await runMultipartUpload({
        file: entry.file,
        initResp,
        onPartComplete: (done, total) => patchFile(entry.id, { progress: { done, total } }),
        isCancelledRef: cancelledRef,
      });

      await axios.post(`${API_URL}/api/raw/complete-upload`, {
        id: initResp.id,
        s3_key: initResp.s3_key,
        upload_id: initResp.upload_id,
        parts,
      });
      patchFile(entry.id, { status: 'done' });
    } catch (err) {
      // Abort so S3 doesn't hoard orphan parts
      if (initResp) {
        try {
          await axios.post(`${API_URL}/api/raw/abort-upload`, {
            id: initResp.id, s3_key: initResp.s3_key, upload_id: initResp.upload_id,
          });
        } catch (_) { /* fine */ }
      }
      if (cancelledRef.current) {
        patchFile(entry.id, { status: 'cancelled' });
      } else {
        patchFile(entry.id, { status: 'error', error: err.response?.data?.detail || err.message || 'Upload failed' });
      }
    }
  };

  const startAll = async () => {
    if (!canStart) return;
    setRunning(true);
    cancelledRef.current = false;
    // Grab a snapshot of current pending entries; upload sequentially.
    // Fresh entries added mid-run are ignored — user hits Start again for those.
    const initial = queue.filter((q) => q.status === 'pending');
    for (const entry of initial) {
      if (cancelledRef.current) break;
      // Read latest state to skip if user removed it mid-run.
      const stillPending = queue.find((q) => q.id === entry.id)?.status === 'pending';
      if (!stillPending) continue;
      await uploadOne(entry);
    }
    setRunning(false);
  };

  const cancelAll = () => { cancelledRef.current = true; };

  const clearFinished = () => setQueue((prev) => prev.filter((q) => q.status !== 'done' && q.status !== 'cancelled'));

  // Derived aggregate progress for the queue
  const queueStats = useMemo(() => {
    let done = 0, uploading = 0, pending = 0, errored = 0;
    for (const q of queue) {
      if (q.status === 'done') done++;
      else if (q.status === 'uploading') uploading++;
      else if (q.status === 'pending') pending++;
      else if (q.status === 'error') errored++;
    }
    return { done, uploading, pending, errored, total: queue.length };
  }, [queue]);

  // ---- Mobile block screen ----
  if (isMobile && !overrideMobile) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-neutral-950 to-black px-4 py-10 text-white">
        <div className="max-w-md mx-auto">
          <Brand />
          <Card className="border-white/10 bg-white/[0.03] backdrop-blur">
            <CardContent className="pt-8 pb-6 text-center space-y-5">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary/15 flex items-center justify-center">
                <Monitor className="w-8 h-8 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-black uppercase tracking-tight mb-2">Use a Computer</div>
                <p className="text-white/70 text-sm leading-relaxed">
                  Raw camera files can be tens of gigabytes. Uploading from a
                  phone is unreliable — the browser may pause the upload if you
                  switch apps or the screen sleeps.
                </p>
                <p className="text-white/70 text-sm leading-relaxed mt-3">
                  Please open this page on your <span className="text-white font-semibold">laptop or desktop</span>:
                </p>
                <div className="mt-3 px-3 py-2 rounded-lg bg-white/5 border border-white/10 font-mono text-primary text-sm break-all">
                  {typeof window !== 'undefined' ? `${window.location.host}/raw` : 'elevatedarena.com/raw'}
                </div>
              </div>
              <div className="pt-2 border-t border-white/10">
                <p className="text-xs text-white/40 mb-3">
                  Uploading a small clip (&lt; 200 MB)? You can override:
                </p>
                <Button
                  onClick={() => setOverrideMobile(true)}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white/70"
                  data-testid="raw-mobile-override"
                >
                  Continue on this device anyway
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-neutral-950 to-black px-4 py-10 text-white">
      <div className="max-w-2xl mx-auto">
        <Brand />

        {!authed ? (
          <Card className="border-white/10 bg-white/[0.03] backdrop-blur">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-primary/15 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-black uppercase tracking-tight">Camera Drop</CardTitle>
                  <CardDescription>Camera operators only. Enter your passcode to continue.</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={verifyPasscode} className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Passcode</label>
                  <Input
                    type="text"
                    inputMode="text"
                    autoComplete="one-time-code"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    onPaste={(e) => {
                      // Force paste to work even if the browser tries to block it.
                      const text = (e.clipboardData || window.clipboardData)?.getData('text');
                      if (text) {
                        e.preventDefault();
                        setPasscode(text.trim());
                      }
                    }}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 text-lg tracking-wider"
                    placeholder="Enter or paste passcode"
                    autoFocus
                    data-testid="raw-passcode-input"
                  />
                </div>
                {passcodeError && (
                  <Alert className="border-red-500/40 bg-red-500/10">
                    <AlertDescription className="text-red-200 text-sm">{passcodeError}</AlertDescription>
                  </Alert>
                )}
                <Button
                  type="submit"
                  disabled={verifying}
                  className="w-full bg-primary text-primary-foreground font-bold py-6"
                  data-testid="raw-passcode-submit"
                >
                  {verifying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…</> : 'Continue'}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-white/10 bg-white/[0.03] backdrop-blur">
            <CardHeader>
              <CardTitle className="text-xl font-black uppercase tracking-tight">Camera Drop</CardTitle>
              <CardDescription>Drop as many files as you want. They all share the same event info below and upload one after another.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Your name</label>
                <Input
                  value={uploaderName}
                  onChange={(e) => setUploaderName(e.target.value)}
                  placeholder="e.g. John Smith"
                  disabled={running}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
                  data-testid="raw-uploader-name"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">
                  What is this video? <span className="text-primary">*</span>
                </label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Heading horses – round 2, box cam"
                  rows={2}
                  disabled={running}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40 resize-none"
                  data-testid="raw-description"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">
                    Location <span className="text-primary">*</span>
                  </label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. Innisfail Rodeo Grounds"
                    disabled={running}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
                    data-testid="raw-location"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">
                    Date &amp; time <span className="text-primary">*</span>
                  </label>
                  <Input
                    type="datetime-local"
                    value={eventDateTime}
                    onChange={(e) => setEventDateTime(e.target.value)}
                    disabled={running}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
                    data-testid="raw-event-datetime"
                  />
                </div>
              </div>

              {/* File drop zone — supports drag+drop + click-to-choose */}
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Files</label>
                <label
                  htmlFor="raw-file-input"
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  className={`flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed rounded-xl cursor-pointer transition
                    ${dragOver ? 'border-primary bg-primary/10' : 'border-white/20 hover:border-primary/60 hover:bg-primary/5'}`}
                  data-testid="raw-file-drop"
                >
                  <Upload className="w-8 h-8 text-white/40" />
                  <div className="text-sm text-white/70">
                    <span className="text-primary font-semibold">Click to choose files</span> or drag and drop them here
                  </div>
                  <div className="text-xs text-white/40">Multiple files OK — video, photo, raw camera cards</div>
                  <input
                    id="raw-file-input"
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={onFilePick}
                    accept="video/*,image/*,.mxf,.mov,.mp4,.mkv,.avi,.raw,.arw,.cr2,.cr3,.dng,.braw,.r3d"
                    data-testid="raw-file-input"
                  />
                </label>
              </div>

              {/* File queue */}
              {queue.length > 0 && (
                <div className="space-y-2" data-testid="raw-file-queue">
                  <div className="flex items-center justify-between text-xs text-white/50">
                    <span>
                      {queueStats.total} file{queueStats.total !== 1 ? 's' : ''}
                      {' · '}
                      <span className="text-emerald-400">{queueStats.done} done</span>
                      {queueStats.uploading > 0 && <>{' · '}<span className="text-primary">{queueStats.uploading} uploading</span></>}
                      {queueStats.pending > 0 && <>{' · '}<span className="text-white/70">{queueStats.pending} waiting</span></>}
                      {queueStats.errored > 0 && <>{' · '}<span className="text-red-400">{queueStats.errored} failed</span></>}
                    </span>
                    {(queueStats.done + (queue.filter((q) => q.status === 'cancelled').length)) > 0 && !running && (
                      <button
                        onClick={clearFinished}
                        className="text-white/50 hover:text-white underline"
                        data-testid="raw-clear-finished"
                      >
                        Clear finished
                      </button>
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto pr-1 space-y-2">
                    {queue.map((q) => {
                      const pct = q.progress.total > 0 ? Math.round((q.progress.done / q.progress.total) * 100) : 0;
                      const doneBytes = Math.min(q.file.size, q.progress.done * RAW_CHUNK_HINT);
                      const statusColor = q.status === 'done' ? 'border-emerald-500/40 bg-emerald-500/5'
                        : q.status === 'error' ? 'border-red-500/40 bg-red-500/5'
                        : q.status === 'uploading' ? 'border-primary/40 bg-primary/5'
                        : q.status === 'cancelled' ? 'border-white/10 bg-white/5 opacity-60'
                        : 'border-white/10 bg-white/[0.03]';
                      return (
                        <div key={q.id} className={`p-3 border rounded-lg ${statusColor}`} data-testid={`raw-queue-item-${q.id}`}>
                          <div className="flex items-center gap-3">
                            <Video className="w-5 h-5 text-white/50 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm truncate" title={q.file.name}>{q.file.name}</div>
                              <div className="text-xs text-white/50 flex items-center gap-2 mt-0.5">
                                <span>{fmtBytes(q.file.size)}</span>
                                {q.status === 'pending' && <span>· Waiting</span>}
                                {q.status === 'uploading' && q.progress.total === 0 && (
                                  <span className="flex items-center gap-1 text-primary"><Loader2 className="w-3 h-3 animate-spin" /> Preparing…</span>
                                )}
                                {q.status === 'uploading' && q.progress.total > 0 && (
                                  <span className="text-primary">· {fmtBytes(doneBytes)} of {fmtBytes(q.file.size)}</span>
                                )}
                                {q.status === 'done' && <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Uploaded</span>}
                                {q.status === 'cancelled' && <span className="text-white/50">· Cancelled</span>}
                                {q.status === 'error' && <span className="text-red-400" title={q.error}>· Failed</span>}
                              </div>
                            </div>
                            {q.status === 'uploading' && q.progress.total > 0 && (
                              <span className="text-xs font-mono text-primary font-bold shrink-0">{pct}%</span>
                            )}
                            {(q.status === 'pending' || q.status === 'error' || q.status === 'cancelled') && !running && (
                              <button
                                onClick={() => removeFile(q.id)}
                                className="p-1.5 hover:bg-white/10 rounded-lg shrink-0"
                                aria-label="Remove file"
                                data-testid={`raw-remove-${q.id}`}
                              >
                                <X className="w-4 h-4 text-white/60" />
                              </button>
                            )}
                          </div>
                          {q.status === 'uploading' && q.progress.total > 0 && (
                            <Progress value={pct} className="h-1.5 mt-2" />
                          )}
                          {q.status === 'error' && q.error && (
                            <div className="mt-2 text-xs text-red-300 flex items-start gap-1">
                              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                              <span className="break-words">{q.error}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              {running ? (
                <Button
                  variant="outline"
                  onClick={cancelAll}
                  className="w-full border-white/20 text-white/70 hover:text-white py-6"
                  data-testid="raw-cancel-all"
                >
                  Cancel remaining uploads
                </Button>
              ) : (
                <Button
                  onClick={startAll}
                  disabled={!canStart}
                  className="w-full bg-primary text-primary-foreground font-bold py-6"
                  data-testid="raw-start-upload"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {queueStats.errored > 0 || queue.some((q) => q.status === 'cancelled') ? 'Start / Retry uploads' : 'Start upload'}
                  {queueStats.pending > 0 ? ` (${queueStats.pending})` : ''}
                </Button>
              )}

              <p className="text-xs text-white/40 text-center">
                Uploads go directly to Elevated Roping Media&apos;s cloud storage. Keep this tab open until every file is marked <span className="text-emerald-400">Uploaded</span>.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
